import {
  COLLAB_PAID_BRIEF_MAX_AMOUNT_MINOR_UNITS,
  CollabType,
  paidBriefMaxEndDays,
  ugcPaymentShortfall,
  type BriefTemplateContent,
} from '@fluenceur/types';

/**
 * What stops a paid brief from being sent.
 *
 * Three bounds, and all three exist because the platform is about to hold the
 * brand's money: a collaboration with no end deadline has nothing to end the
 * hold, one that runs past sixty days holds the money longer than the rail is
 * allowed to, and one above the cap is more than the rail carries today.
 *
 * The bounds themselves come from the shared contract, so the composer and the
 * send route refuse on exactly the same numbers rather than on two copies of
 * them.
 *
 * A UGC brief also pays at least its minimum per content, the very rule the
 * send refuses with (`ugcPaymentShortfall`), so the wizard warns before the
 * send rather than after it, and ends 45 days out at the latest
 * (`paidBriefMaxEndDays`), its own sentence since sixty stays true elsewhere.
 *
 * A brief that promises no money is held to none of this: a gifted product is
 * handed over by the brand itself and never travels through the rail.
 */
export type CollabPaidBriefViolation =
  | 'endDeadlineRequired'
  | 'endDeadlineTooFar'
  | 'ugcEndDeadlineTooFar'
  | 'amountAboveCap'
  | 'ugcBelowMinimum';

export const collabPaidBriefViolations = (
  content: BriefTemplateContent,
  collabType?: CollabType | null
): CollabPaidBriefViolation[] => {
  const { compensation, timeline } = content;
  if (compensation.type !== 'fixed_payment') return [];

  const violations: CollabPaidBriefViolation[] = [];
  const endDays = timeline?.endDays ?? null;

  if (endDays === null) violations.push('endDeadlineRequired');
  else if (endDays > paidBriefMaxEndDays(collabType)) {
    violations.push(collabType === CollabType.UGC ? 'ugcEndDeadlineTooFar' : 'endDeadlineTooFar');
  }

  if ((compensation.fixedPayment?.amountCents ?? 0) > COLLAB_PAID_BRIEF_MAX_AMOUNT_MINOR_UNITS) {
    violations.push('amountAboveCap');
  }

  if (ugcPaymentShortfall(content)) violations.push('ugcBelowMinimum');

  return violations;
};
